import { ConsumerSettingsService } from "@/shared-services/consumer/settings.service";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { ApiService } from "@/shared-models/consumer/IApiService";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import {
  SettingValue,
  Settings,
  DEFAULT_SETTING,
} from "@/shared-models/consumer/ISetting";
import { requestBiometricAuth } from "@/apps/credit-line/helpers/biometricBridge";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { isAndroid } from "@/apps/credit-line/helpers/platformChecker.helper";

declare const Android: AndroidInterface;

/**
 * Consumer Settings helper for Credit Line app
 * Provides utility functions to get consumer settings
 */

/**
 * Create a settings service instance
 * @param categoryKey Optional settings category
 * @returns ConsumerSettingsService instance
 */
export const createSettingsService = (
  categoryKey?: SettingsCategory
): ConsumerSettingsService => {
  const apiService = new Service();
  return new ConsumerSettingsService(
    apiService as ApiService,
    AuthenticationHelper,
    DatadogLogger,
    categoryKey
  );
};

/**
 * Get a specific setting by name
 * @param settingName The name of the setting to retrieve
 * @param categoryKey Optional settings category
 * @param deviceTag
 * @returns Promise<SettingValue>
 */
export const getSetting = async (
  settingName: string,
  categoryKey?: SettingsCategory,
  deviceTag?: string
): Promise<SettingValue> => {
  try {
    const settingsService = createSettingsService(categoryKey);
    return await settingsService.getSetting(settingName, deviceTag);
  } catch (error) {
    console.error(`Failed to load setting ${settingName}:`, error);
    return DEFAULT_SETTING;
  }
};

/**
 * Get all settings for a category
 * @param categoryKey Optional settings category
 * @returns Promise<Settings>
 */
export const getSettings = async (
  categoryKey?: SettingsCategory
): Promise<Settings> => {
  try {
    const settingsService = createSettingsService(categoryKey);
    return await settingsService.getSettings(categoryKey);
  } catch (error) {
    console.error("Failed to load settings:", error);
    return {};
  }
};

/**
 * Reads a setting and, if `values.requiresBiometricAuthentication === true`,
 * triggers biometric auth via Android bridge.
 */
export const BIOMETRIC_REASON_NOT_AVAILABLE =
  "BIOMETRICS_NOT_AVAILABLE" as const;
export const BIOMETRIC_REASON_CHECK_FAILED = "BIOMETRIC_CHECK_FAILED" as const;

type BiometricFailureReason =
  | typeof BIOMETRIC_REASON_NOT_AVAILABLE
  | typeof BIOMETRIC_REASON_CHECK_FAILED;

export type EnsureBiometricResult =
  | { success: true }
  | { success: false; reason: BiometricFailureReason };

export const ensureBiometricForSetting = async (
  settingName: string,
  categoryKey: SettingsCategory,
  deviceTag?: string
): Promise<EnsureBiometricResult> => {
  let setting: SettingValue;

  try {
    setting = await getSetting(settingName, categoryKey, deviceTag);
  } catch (e) {
    console.error("Failed to load setting for biometric check", {
      settingName,
      categoryKey,
      error: e,
    });
    // If we can't even load the setting, safest is to BLOCK.
    return { success: false, reason: BIOMETRIC_REASON_CHECK_FAILED };
  }

  const requiresBio =
    !!setting.values?.requiresBiometricAuthentication &&
    setting.enabled !== false;

  if (!requiresBio) {
    // No biometric required: allow
    return { success: true };
  }

  const biometricAvailable =
    isAndroid() &&
    typeof Android !== "undefined" &&
    typeof Android.requestBiometricAuthentication === "function";

  if (!biometricAvailable) {
    return { success: false, reason: BIOMETRIC_REASON_NOT_AVAILABLE };
  }

  // Biometric required
  try {
    const ok = await requestBiometricAuth();
    return ok
      ? { success: true }
      : { success: false, reason: BIOMETRIC_REASON_CHECK_FAILED };
  } catch (e) {
    console.error("Biometric auth threw an error", e);
    return { success: false, reason: BIOMETRIC_REASON_CHECK_FAILED };
  }
};
